import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

export function getBuildStageEndPoint(service: string): string {
  switch (DEPLOYMENT_TARGET) {
    case 'prod':
      return `https://${service}.cloudone.trendmicro.com/api`;
    case 'stage':
      return `https://${service}.staging-cloudone.trendmicro.com/api`;
    case 'drsim':
      return `https://${service}.drsim-cloudone.trendmicro.com/api`;
    case 'govcloud-prod':
      return `https://${service}.cloudonegov.trendmicro.com/api`;
    case 'govcloud-staging':
      return `https://${service}.staging-cloudonegov.trendmicro.com/api`;
    case 'alpha':
    case 'dev':
      return window.location.hostname === 'localhost'
        ? `/api`
        : `https://${service}.dev-cloudone.trendmicro.com/api`;
    default:
      console.error('No base url found for deployment target', DEPLOYMENT_TARGET);
      throw new Error('No base url found for deployment target.');
  }
}

const authAxios = axios.create({
  baseURL: process.env.API_URL,
});

authAxios.interceptors.request.use(
  (config) => {
    const c1Token = localStorage.getItem('c1Token');
    config.headers.Authorization = `Bearer ${c1Token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

authAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const code = error && error.response ? error.response.status : 0;
    if (code === 401 || code === 403) {
      console.log('error code', code);
    }
    return Promise.reject(error);
  }
);

/** When axios api calls return with >= 400 response they throw Error.
 * Determine if axios error by checking for`isAxiosError` field and that the response isn't undefined
 */
function isAxiosError(error: unknown): error is AxiosError {
  if (!error || typeof error !== 'object') return false;
  return (
    !!(error as AxiosError | null)?.isAxiosError &&
    (error as AxiosError | null)?.response !== undefined
  );
}

/**
 * Returns rejected promise for errors >= 400. Rejected promise is undefined for 403
 * @param API
 * @param url
 * @param config
 */
export async function requestDelete<T>(
  API: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig
): Promise<T | null> {
  try {
    const { data, status } = await API.delete<T>(url, config);
    // 204 status means no content returned. Use null because data contains empty string
    return status === 204 ? null : data;
  } catch (error: unknown) {
    // API call successfully made and returned but response was >= 400
    if (isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      return Promise.reject({ status, message: data?.message || error.message });
    }
    // If no response then something prevented an api call from being made. Like network connectivity or other code in the try. Rethrow and handle further up.
    throw error;
  }
}

/** Remove non-uuid characters from a string. Only allow alphanumeric + '-' + '_'  */
export function stripNonUUID(str: string): string {
  return str.replace(/[^a-zA-Z0-9-_]+/gi, '');
}

export { authAxios };
